/**
 * FALL PHASE — orchestrates the fall stage.
 *
 * Objects are spawned at the top of the screen according to the slot-based
 * timeline (same layout.js as before). They free-fall under physics.
 * The flask sits at the bottom and can move left/right via input.
 *
 * Events emitted:
 *   fall:start, fall:tick, fall:countdown_finished, fall:complete, fall:failed
 *   fall:boulder  — first boulder-flask collision (damage or shield consumed)
 *   fall:shield_block — boulder hit while shield was active (shield consumed, no HP loss)
 *   fall:bounce   — non-boulder object hits flask side wall
 *   fall:suction  — object enters the flask mouth suction zone
 *   fall:gem      — gem collected via suction
 *   fall:bonus    — shield collected via suction
 *   fall:catch    — merge pickup collected via suction
 *   fall:revive   — rewarded revive restored play
 *   fall:miss     — merge pickup pruned off-screen without being caught
 *
 * Options may include `onDrawFlask` and `onDrawStructuralAmbient` (full-canvas glue after the flask column each frame).
 */
import * as C from "../config/constants.js";
import { computeFallDrawTopOverflowPx } from "../config/constants.js";
import { EventBus } from "../core/event-bus.js";
import { InputManager } from "../core/input-manager.js";
import { RunPlayer } from "./objects/Player.js";
import { FallPhysics } from "./fall-physics.js";
import { FallRenderer } from "./fall-renderer.js";
import {
  FLASK_SHARD_BURST_COUNT_MAX,
  FLASK_SHARD_BURST_COUNT_MIN,
  FLASK_SHARD_BURST_DAMAGE_COUNT_MAX,
  FLASK_SHARD_BURST_DAMAGE_COUNT_MIN,
  createFlaskShardBurst,
  getFlaskDamageSeverity,
  getFlaskShardGravityPx,
  pruneFlaskShardBursts,
} from "../flask/flask-damage-shards.js";
import { generateRunLayout, createRunwayObjects, createMergePickups } from "./layout.js";
import { stepFallCallout } from "./fall-callouts.js";
import {
  getFallCountdownTickSec,
  shouldSkipFallBonusSpawn,
  shouldSkipFallBoulderSpawn,
} from "./fall-run-config.js";
import { getFallMouthHalfWidthPx } from "./fall-flask-shape.js";

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

/** Run-local Y of the catcher flask neck or bottom (same dims as `FallRenderer._getFlaskDims`). */
function getFlaskAnchorY(phase, kind) {
  var flaskH;
  var flaskTopY;
  if (phase.fallRenderer && typeof phase.fallRenderer._getFlaskDims === "function") {
    var dims = phase.fallRenderer._getFlaskDims(phase.runHeight);
    flaskH = dims.flaskH;
    flaskTopY = dims.flaskTopY;
  } else {
    var scale = phase.scale;
    flaskH = scale && scale.runwayFlaskDisplayHeight ? scale.runwayFlaskDisplayHeight : 120;
    var topFromBottom = C.FLASK_TOP_FROM_BOTTOM_RATIO != null
      ? C.FLASK_TOP_FROM_BOTTOM_RATIO
      : 0.24;
    var desiredTopY = phase.runHeight * (1 - topFromBottom);
    var bottomGap = phase.runHeight * 0.018;
    flaskTopY = Math.min(desiredTopY, phase.runHeight - flaskH - bottomGap);
    flaskTopY = Math.max(0, flaskTopY);
  }
  return kind === "bottom" ? flaskTopY + flaskH : flaskTopY;
}

function getMaxFrameDeltaMs(runConfig) {
  if (runConfig && runConfig.MAX_FRAME_DELTA_MS != null) {
    return runConfig.MAX_FRAME_DELTA_MS;
  }
  return C.MAX_FRAME_DELTA_MS != null
    ? C.MAX_FRAME_DELTA_MS
    : 100;
}

  function getRunEndAftermathMs(runConfig) {
    return runConfig && runConfig.FALL_SETTLE_MS != null
      ? runConfig.FALL_SETTLE_MS
      : 2000;
  }

export class FallPhase {
    constructor(canvas, ctx, onComplete, options) {
      this.canvas = canvas;
      this.ctx = ctx;
      this.onComplete = onComplete || function () {};
      this.options = options || {};
      this.runConfig = this.options.runConfig || (C.RUN_CONFIG || {});
      var cw = canvas ? canvas.width : 800;
      var ch = canvas ? canvas.height : 500;
      this.runWidth = this.options.runWidth != null ? this.options.runWidth : cw;
      this.runHeight = this.options.runHeight != null ? this.options.runHeight : ch;
      this.runOriginX = this.options.runOriginX != null ? this.options.runOriginX : 0;
      this.runOriginY = this.options.runOriginY != null ? this.options.runOriginY : 0;
      this.scale = this.options.scale || null;
      this.mockupScale = this.options.mockupScale || null;

      this.fallRenderer = new FallRenderer(this.runConfig, this.scale, this.mockupScale);
      this.fallPhysics = null; // created in start()

      this.state = "idle";
      this.player = null;
      this.pendingObjects = []; // objects waiting to be spawned (sorted by spawnAtTime)
      this.gems = 0;
      this.growthCount = 0;
      this.timeRemaining = this.runConfig.RUN_DURATION || 20;
      this.elapsed = 0;
      this.lastTime = 0;
      this.animationFrameId = null;
      this._boundGameLoop = this.gameLoop.bind(this);
      this.inputManager = null;

      this.countdownRemaining = this.runConfig.RUN_COUNTDOWN_SECONDS || 0;
      this.catchFlashUntil = 0;
      this.isFlaskShattered = false;
      this.shatterEndsAt = 0;
      this.isRunEndingAftermath = false;
      this.runEndAftermathEndsAt = 0;
      this.eventBus = EventBus || null;
      this.movementHintSeen = false;
      this._movementHintDomVisible = false;
      this.countdownStartedAt = null;
      this.playingStartedAt = null;
      this.isPaused = false;
      this.pausedAt = 0;
      this.totalPausedMs = 0;
      this.reviveOfferActive = false;
      this.reviveGraceEndsAt = 0;
      this._shatterGraceOutcome = null;
      this._flaskShardBursts = [];
      this._fallStartHp = null;
      this._resetFallCallouts();
    }

    _shouldOfferMovementHint() {
      return this.options.showMovementHint !== false;
    }

    _finishCountdown(currentTime) {
      if (this.state === "playing") return;
      this.state = "playing";
      this.countdownRemaining = 0;
      this.playingStartedAt = currentTime != null && isFinite(currentTime)
        ? currentTime
        : (typeof performance !== "undefined" ? performance.now() : Date.now());
      if (this._shouldOfferMovementHint()) this.movementHintSeen = true;
      if (this.eventBus) {
        this.eventBus.emit("fall:countdown_finished", {});
        this.eventBus.emit("fall:tick", this.getStatus());
      }
      if (typeof this.options.onGameplayStart === "function") this.options.onGameplayStart();
    }

    _isMovementHintVisible() {
      if (this.isRunEndingAftermath || this.isFlaskShattered) return false;
      if (this.state !== "countdown") return false;
      if (!this._shouldOfferMovementHint() || this.movementHintSeen) return false;
      return true;
    }

    _syncMovementHintDom() {
      var visible = this._isMovementHintVisible();
      if (visible === this._movementHintDomVisible) return;
      this._movementHintDomVisible = visible;
      if (typeof this.options.onMovementHintVisibleChange === "function") {
        this.options.onMovementHintVisibleChange(visible);
      }
    }

    _shouldShowMovementRange() {
      if (typeof this.options.getShowMovementRange === "function") {
        if (!this.options.getShowMovementRange()) return false;
      } else if (this.options.showMovementRange === false) {
        return false;
      }
      if (this.state !== "countdown" && this.state !== "playing") return false;
      return true;
    }

    _getMouthHalfWidth() {
      var wallW = this.scale && this.scale.flaskWallThickness ? this.scale.flaskWallThickness : undefined;
      var mouthHalfW = getFallMouthHalfWidthPx({
        flaskGeometry: this.fallPhysics && this.fallPhysics.flaskGeometry,
        scale: this.scale,
        runWidth: this.runWidth,
        runHeight: this.runHeight,
        runConfig: this.runConfig,
        wallThicknessPx: wallW,
        cx: this.player ? this.player.x : this.runWidth / 2,
      });
      if (mouthHalfW != null) return mouthHalfW;
      return this.player ? this.player.hp : 0;
    }

    /**
     * Place the catcher under the last known cursor (if any) so stage start does not
     * snap from centre on the first mousemove.
     * @returns {number|null} Seeded clientX when applied, else null.
     */
    _applyInitialPointerAim() {
      if (!this.player) return null;
      var clientX = null;
      if (typeof this.options.getInitialPointerClientX === "function") {
        clientX = this.options.getInitialPointerClientX();
      }
      if (clientX == null || !isFinite(Number(clientX))) return null;
      clientX = Number(clientX);
      if (typeof this.options.clientXToBoardX !== "function") return null;
      var targetX = this.options.clientXToBoardX(clientX) - (this.runOriginX || 0);
      if (!isFinite(targetX)) return null;
      var mouthHalfW = this._getMouthHalfWidth();
      this.player.x = clamp(targetX, mouthHalfW, this.runWidth - mouthHalfW);
      return clientX;
    }

    start() {
      var cfg = this.runConfig;
      var genLayout = this.options.generateRunLayout || generateRunLayout;
      var mkRunway = this.options.createRunwayObjects || createRunwayObjects;
      var mkMerge = this.options.createMergePickups || createMergePickups;

      this.player = new RunPlayer(this.runWidth / 2, this.runHeight - 100, this.scale, cfg);
      if (this.options.startingHp != null) {
        this.player.hp = clamp(Number(this.options.startingHp), this.player.minHp, this.player.maxHp);
      }
      this._fallStartHp = this.player.hp;
      var seededPointerClientX = this._applyInitialPointerAim();

      var layoutResult = genLayout(cfg);
      var runObjects = mkRunway(layoutResult, cfg);
      var pickSpawnTier = this.options.pickSpawnTier || function () { return 0; };
      var bounds = layoutResult && layoutResult.spawnBounds
        ? layoutResult.spawnBounds : { minProgress: 0, maxProgress: 1 };
      var mergePickups = mkMerge(layoutResult, cfg, bounds.minProgress, bounds.maxProgress, pickSpawnTier);

      // All objects are pending until their spawnAtTime is reached
      var allObjects = runObjects.concat(mergePickups);
      // Sort by spawnAtTime ascending
      allObjects.sort(function (a, b) {
        return (a.spawnAtTime || 0) - (b.spawnAtTime || 0);
      });
      this.pendingObjects = allObjects;

      this.gems = 0;
      this.growthCount = 0;
      this.timeRemaining = cfg.RUN_DURATION || 20;
      this.elapsed = 0;
      this.lastTime = 0;
      this.countdownRemaining = cfg.RUN_COUNTDOWN_SECONDS || 0;
      this.catchFlashUntil = 0;
      this.isFlaskShattered = false;
      this.shatterEndsAt = 0;
      this.isRunEndingAftermath = false;
      this.runEndAftermathEndsAt = 0;

      this.movementHintSeen = !this._shouldOfferMovementHint();
      this.countdownStartedAt = null;
      this.playingStartedAt = null;
      this.isPaused = false;
      this.pausedAt = 0;
      this.totalPausedMs = 0;
      this.reviveOfferActive = false;
      this.reviveGraceEndsAt = 0;
      this._shatterGraceOutcome = null;
      this._flaskShardBursts = [];
      this._resetFallCallouts();

      var evolutionChain = typeof C.getActiveEvolutionChain === "function"
        ? C.getActiveEvolutionChain()
        : (C.EVOLUTION_CHAIN || []);
      var self = this;
      this.fallPhysics = new FallPhysics({
        runWidth: this.runWidth,
        runHeight: this.runHeight,
        evolutionChain: evolutionChain,
        scale: this.scale,
        runConfig: cfg,
        eventBus: this.eventBus,
        onBoulderHit: this._onBoulderHit.bind(this),
        isShieldActive: function () {
          return !!(self.player && self.player.hasShield);
        },
      });
      // Initialize the physics engine with the player's starting X
      this.fallPhysics.ensureEngine(this.player.x);

      if (this.countdownRemaining > 0) {
        this.state = "countdown";
        this.countdownStartedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
      } else {
        var now = typeof performance !== "undefined" ? performance.now() : Date.now();
        this._finishCountdown(now);
      }

      var InputManagerCtor = this.options.InputManager || InputManager;
      if (InputManagerCtor) {
        var moveSpeedRatio = cfg.PLAYER_MOVE_SPEED_RATIO != null ? cfg.PLAYER_MOVE_SPEED_RATIO : 0.6;
        var baseMoveSpeed = this.runWidth * moveSpeedRatio;
        var minRatio = cfg.PLAYER_KEYBOARD_MOVE_SPEED_MIN_RATIO != null
          ? cfg.PLAYER_KEYBOARD_MOVE_SPEED_MIN_RATIO
          : 1;
        var maxRatio = cfg.PLAYER_KEYBOARD_MOVE_SPEED_MAX_RATIO != null
          ? cfg.PLAYER_KEYBOARD_MOVE_SPEED_MAX_RATIO
          : 2;
        var rampMs = cfg.PLAYER_KEYBOARD_MOVE_RAMP_MS != null
          ? cfg.PLAYER_KEYBOARD_MOVE_RAMP_MS
          : 1000;
        this.inputManager = new InputManagerCtor({
          pointerSensitivity: cfg.POINTER_SENSITIVITY,
          playerMoveSpeed: baseMoveSpeed,
          keyboardMoveSpeedMin: baseMoveSpeed * minRatio,
          keyboardMoveSpeedMax: baseMoveSpeed * maxRatio,
          keyboardMoveRampMs: rampMs,
          getCssToGameScale: this.options.getCssToGameScale,
          onArrowKey: typeof this.options.onArrowKey === "function"
            ? this.options.onArrowKey
            : null,
          onGameplayInput: typeof this.options.onGameplayInput === "function"
            ? this.options.onGameplayInput
            : null,
        });
        this.inputManager.attach();
        if (seededPointerClientX != null
          && typeof this.inputManager.seedPointerClientX === "function") {
          this.inputManager.seedPointerClientX(seededPointerClientX);
        }
      }

      if (this.eventBus) this.eventBus.emit("fall:start", { runWidth: this.runWidth, runHeight: this.runHeight });

      this.animationFrameId = requestAnimationFrame(this._boundGameLoop);
    }

    stop() {
      this.state = "idle";
      if (this.inputManager) {
        this.inputManager.detach();
        this.inputManager = null;
      }
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      if (this.fallPhysics) {
        this.fallPhysics.destroy();
        this.fallPhysics = null;
      }
    }

    getStatus() {
      var runDuration = this.runConfig && this.runConfig.RUN_DURATION != null ? this.runConfig.RUN_DURATION : 20;
      var displayTimeRemaining = this.state === "countdown"
        ? Math.ceil(runDuration)
        : Math.max(0, Math.ceil(this.timeRemaining));
      return {
        timeRemaining: displayTimeRemaining,
        timeRemainingExact: Math.max(0, this.timeRemaining),
        gems: this.gems,
        hpPercentage: this.player ? this.player.getHpPercentage() : 100,
        playerHp: this.player ? this.player.hp : 0,
        state: this.state,
        countdownRemaining: this.countdownRemaining,
        flaskCount: typeof this.options.getFlaskCount === "function" ? this.options.getFlaskCount() : 0,
        isPaused: !!this.isPaused,
      };
    }

    getResults() {
      return {
        gems: this.gems,
        finalHp: this.player ? this.player.getHpPercentage() : 100,
        playerHp: this.player ? this.player.hp : 25,
        growthCount: Math.max(0, Math.floor(Number(this.growthCount) || 0)),
      };
    }

    gameLoop(currentTime) {
      if (this.state === "idle" || this.state === "finished") return;
      var workStart = typeof performance !== "undefined" ? performance.now() : currentTime;
      try {
        this._gameLoopStep(currentTime);
      } catch (err) {
        console.error("[FallPhase] gameLoop error:", err);
      }
      if (typeof this.options.onFrameWork === "function") {
        var workNow = typeof performance !== "undefined" ? performance.now() : currentTime;
        this.options.onFrameWork({
          cpuMs: workNow - workStart,
          currentTime: currentTime,
          skyMs: this._devFrameSkyMs || 0,
          physMs: this._devFramePhysMs || 0,
          drawMs: this._devFrameDrawMs || 0,
        });
      }
      if (this.state !== "finished" && this.state !== "failed") {
        this.animationFrameId = requestAnimationFrame(this._boundGameLoop);
      }
    }

    _gameLoopStep(currentTime) {
      this._devFrameSkyMs = 0;
      this._devFramePhysMs = 0;
      this._devFrameDrawMs = 0;
      var cfg = this.runConfig;
      if (this.lastTime === 0) this.lastTime = currentTime;
      var rawDeltaMs = currentTime - this.lastTime;
      this.lastTime = currentTime;
      var deltaMs = Math.max(0, Math.min(getMaxFrameDeltaMs(cfg), rawDeltaMs || 0));
      var deltaTime = deltaMs / 1000;

      if (this.isPaused) {
        if (typeof this.options.onUpdate === "function") {
          this.options.onUpdate(this.getStatus(), {
            deltaMs: 0,
            deltaTime: 0,
            currentTime: currentTime,
          });
        }
        this._render(currentTime);
        return;
      }

      if (this.reviveGraceEndsAt && !this.reviveOfferActive && currentTime >= this.reviveGraceEndsAt) {
        var graceOutcome = this._shatterGraceOutcome;
        this.reviveGraceEndsAt = 0;
        this._shatterGraceOutcome = null;
        if (graceOutcome === "revive") {
          this._enterReviveOffer(currentTime);
          if (this.isPaused) {
            if (typeof this.options.onUpdate === "function") {
              this.options.onUpdate(this.getStatus(), {
                deltaMs: 0,
                deltaTime: 0,
                currentTime: currentTime,
              });
            }
            this._render(currentTime);
            return;
          }
        } else if (graceOutcome === "fail") {
          this.fail("flask_broken");
          return;
        }
      }

      // --- countdown phase ---
      if (this.state === "countdown") {
        this._updatePlayerMovement(deltaTime, currentTime);
        if (this.fallPhysics) {
          this.fallPhysics.updateFlaskPosition(this.player.x);
        }
        this.countdownRemaining -= deltaTime / getFallCountdownTickSec(this.runConfig);
        if (this.countdownRemaining <= 0) {
          this._finishCountdown(currentTime);
        }
        if (typeof this.options.onUpdate === "function") {
          this.options.onUpdate(this.getStatus(), {
            deltaMs: deltaMs,
            deltaTime: deltaTime,
            currentTime: currentTime,
          });
        }
        this._render(currentTime);
        return;
      }

      // --- timer ---
      this.timeRemaining -= deltaTime;
      this.elapsed += deltaTime;
      this._playElapsedMs = (this._playElapsedMs || 0) + deltaMs;
      if (this.timeRemaining < 0) this.timeRemaining = 0;
      if (this.isFlaskShattered && this.shatterEndsAt > 0 && currentTime >= this.shatterEndsAt) {
        this.fail("flask_broken");
        return;
      }
      if (this.isRunEndingAftermath && currentTime >= this.runEndAftermathEndsAt) {
        this.finish();
        return;
      }
      if (this.timeRemaining <= 0 && !this.isRunEndingAftermath) {
        if (this.isFlaskShattered) {
          // Once shattered, run can only end in failure after shatter delay.
          return;
        }
        this._startRunEndingAftermath(currentTime);
        if (this.state === "finished") return;
      }

      this._updatePlayerMovement(deltaTime, currentTime);

      // --- update flask body position to follow player ---
      if (this.fallPhysics) {
        this.fallPhysics.updateFlaskPosition(this.player.x);
      }

      // --- spawn pending objects ---
      if (!this.isRunEndingAftermath) {
        this._spawnDue();
      }

      if (this.fallPhysics) {
        var physStart = typeof performance !== "undefined" ? performance.now() : currentTime;
        this.fallPhysics.checkSuction(currentTime);
        this.fallPhysics.step(deltaMs, currentTime);
        var finished = this.fallPhysics.processSuction(currentTime);
        for (var i = 0; i < finished.length; i++) {
          this._collectObject(finished[i].obj, currentTime);
          if (finished[i].obj.getType && finished[i].obj.getType() === "merge_pickup") {
            this.catchFlashUntil = currentTime + (cfg.RUN_CATCH_FLASH_MS || 200);
          }
        }
        this.fallPhysics.processExplosions(currentTime);
        var pruned = this.fallPhysics.pruneOffscreen();
        for (var j = 0; j < pruned.length; j++) {
          var obj = pruned[j].obj;
          var type = obj.getType ? obj.getType() : "";
          if (type === "merge_pickup" && !obj.caught) {
            if (this.eventBus) this.eventBus.emit("fall:miss", { tier: obj.tier });
          }
        }
        this._devFramePhysMs = (typeof performance !== "undefined" ? performance.now() : currentTime) - physStart;
      }

      if (this.isRunEndingAftermath && this._isSettleVisuallyIdle(currentTime)) {
        this.finish();
        return;
      }

      if (this.eventBus) this.eventBus.emit("fall:tick", this.getStatus());
      if (typeof this.options.onUpdate === "function") {
        this.options.onUpdate(this.getStatus(), {
          deltaMs: deltaMs,
          deltaTime: deltaTime,
          currentTime: currentTime,
        });
      }

      this._tickFallCallout();
      this._render(currentTime);
    }

    _spawnDue() {
      // Spawn all objects whose spawnAtTime has passed
      while (this.pendingObjects.length > 0) {
        var next = this.pendingObjects[0];
        var spawnAt = next.spawnAtTime != null ? next.spawnAtTime : 0;
        if (this.elapsed >= spawnAt) {
          this.pendingObjects.shift();
          var type = next && typeof next.getType === "function" ? next.getType() : "";
          var isBoulder = type === "boulder";
          var isBonus = type === "bonus";
          if (isBoulder && shouldSkipFallBoulderSpawn(this.runConfig, this.player, this._fallStartHp)) {
            continue;
          }
          if (isBonus && shouldSkipFallBonusSpawn(this.runConfig, this.player)) continue;
          if (this.fallPhysics) this.fallPhysics.spawnObject(next);
        } else {
          break;
        }
      }
    }

    /** Drop any in-air shield pickups while the catcher already has a shield. */
    _clearFallingShieldPickups() {
      if (!this.fallPhysics || typeof this.fallPhysics.removeObjectsByType !== "function") return;
      this.fallPhysics.removeObjectsByType("bonus");
    }

    _updatePlayerMovement(deltaTime, currentTime) {
      if (!this.player) return;
      var mouthHalfW = this._getMouthHalfWidth();
      var allowMove = this.state === "playing" || this.state === "countdown";
      var pointerClientX = null;
      if (this.inputManager) {
        if (typeof this.inputManager.getFollowPointerClientX === "function") {
          pointerClientX = this.inputManager.getFollowPointerClientX();
        } else if (typeof this.inputManager.getPointerClientX === "function") {
          pointerClientX = this.inputManager.getPointerClientX();
        }
      }

      var keyboardHeld = this.inputManager
        && typeof this.inputManager.hasKeyboardDirection === "function"
        && this.inputManager.hasKeyboardDirection();

      if (!keyboardHeld && pointerClientX != null && typeof this.options.clientXToBoardX === "function") {
        var targetX = this.options.clientXToBoardX(pointerClientX) - (this.runOriginX || 0);
        if (isFinite(targetX)) {
          this.player.x = clamp(
            targetX,
            mouthHalfW,
            this.runWidth - mouthHalfW
          );
        } else {
          var moveAmount = this.inputManager
            ? this.inputManager.getMovementDelta(deltaTime, allowMove, currentTime)
            : 0;
          this.player.x = clamp(
            this.player.x + moveAmount,
            mouthHalfW,
            this.runWidth - mouthHalfW
          );
        }
      } else {
        var moveAmount = this.inputManager
          ? this.inputManager.getMovementDelta(deltaTime, allowMove, currentTime)
          : 0;
        this.player.x = clamp(
          this.player.x + moveAmount,
          mouthHalfW,
          this.runWidth - mouthHalfW
        );
      }
    }

    _collectObject(obj, currentTime) {
      var type = obj.getType ? obj.getType() : "";

      if (type === "gem") {
        obj.collected = true;
        this.gems += 1;
        if (this.eventBus) this.eventBus.emit("fall:gem", { count: 1 });
      } else if (type === "bonus") {
        obj.collected = true;
        this.player.activateShield(this._getAnimTime(currentTime));
        this._clearFallingShieldPickups();
        if (this.eventBus) this.eventBus.emit("fall:bonus", {});
      } else if (type === "merge_pickup") {
        obj.caught = true;
        this.growthCount++;
        var catchX = this.player ? this.player.x : this.runWidth / 2;
        var catchY = getFlaskAnchorY(this, "neck");
        if (this.eventBus) {
          this.eventBus.emit("fall:catch", {
            tier: obj.tier,
            x: (this.runOriginX || 0) + catchX,
            y: (this.runOriginY || 0) + catchY,
          });
        }
        if (typeof this.options.onCatchToStash === "function") {
          this.options.onCatchToStash(obj.tier);
        }
        this._pendingCreatureCatches = (this._pendingCreatureCatches || 0) + 1;
      }
    }

    /** Wall-clock draw time; frozen while paused (matches physics animation timestamps). */
    _getSimTime(currentTime) {
      if (this.isPaused && this.pausedAt) return this.pausedAt;
      return currentTime;
    }

    /**
     * Looping motion clock (buoy bob). Excludes paused duration so unpause
     * does not jump phase; do not use for suction/spawn tweens tied to physics.
     */
    _getAnimTime(currentTime) {
      var totalPaused = this.totalPausedMs || 0;
      if (this.isPaused && this.pausedAt) {
        return this.pausedAt - totalPaused;
      }
      var t = currentTime != null && isFinite(currentTime)
        ? currentTime
        : (typeof performance !== "undefined" ? performance.now() : Date.now());
      return t - totalPaused;
    }

    pause(currentTime) {
      if (this.state !== "playing" && this.state !== "countdown") return false;
      if (this.isPaused) return true;
      this.isPaused = true;
      this.pausedAt = currentTime != null
        ? currentTime
        : (typeof performance !== "undefined" ? performance.now() : Date.now());
      return true;
    }

    resume(currentTime) {
      if (!this.isPaused) return false;
      var now = currentTime != null
        ? currentTime
        : (typeof performance !== "undefined" ? performance.now() : Date.now());
      var pausedFor = Math.max(0, now - (this.pausedAt || now));
      if (pausedFor > 0) {
        this.totalPausedMs = (this.totalPausedMs || 0) + pausedFor;
        if (this.shatterEndsAt) this.shatterEndsAt += pausedFor;
        if (this.runEndAftermathEndsAt) this.runEndAftermathEndsAt += pausedFor;
        if (this.reviveGraceEndsAt) this.reviveGraceEndsAt += pausedFor;
      }
      this.lastTime = now;
      this.isPaused = false;
      this.pausedAt = 0;
      return true;
    }

    _getShatterDelayMs() {
      return this.runConfig.RUN_FLASK_SHATTER_TO_GAME_OVER_MS != null
        ? this.runConfig.RUN_FLASK_SHATTER_TO_GAME_OVER_MS
        : 2000;
    }

    _getReviveGraceMs() {
      return this.runConfig.REVIVE_OFFER_GRACE_MS != null
        ? this.runConfig.REVIVE_OFFER_GRACE_MS
        : 2000;
    }

    _beginFlaskShatterVisuals() {
      if (this.fallPhysics && typeof this.fallPhysics.disableFlask === "function") {
        this.fallPhysics.disableFlask();
      }
      if (typeof this.options.onFlaskShattered === "function") {
        this.options.onFlaskShattered();
      }
    }

    _canOfferReviveNow() {
      return typeof this.options.canOfferRevive === "function" && this.options.canOfferRevive();
    }

    _beginReviveGrace(currentTime, outcome) {
      this.isFlaskShattered = true;
      this.reviveGraceEndsAt = currentTime + this._getReviveGraceMs();
      this._shatterGraceOutcome = outcome === "revive" ? "revive" : "fail";
      this._beginFlaskShatterVisuals();
    }

    _enterReviveOffer(currentTime) {
      this.reviveOfferActive = true;
      this.reviveGraceEndsAt = 0;
      this.pause(currentTime);
      if (typeof this.options.onReviveOffer === "function") {
        this.options.onReviveOffer({ stage: "fall", context: "live" });
      }
    }

    declineRevive(currentTime) {
      if (!this.reviveOfferActive) return false;
      this.reviveOfferActive = false;
      this.reviveGraceEndsAt = 0;
      this._shatterGraceOutcome = null;
      this.fail("flask_broken");
      return true;
    }

    applyRevive(currentTime) {
      if (!this.reviveOfferActive || !this.fallPhysics || !this.player) return false;
      var now = currentTime != null
        ? currentTime
        : (typeof performance !== "undefined" ? performance.now() : Date.now());
      if (typeof this.fallPhysics.enableFlask === "function") {
        this.fallPhysics.enableFlask(this.player.x);
      }
      var restoreHp = this.runConfig.REVIVE_FALL_RESTORE_HP != null
        ? Number(this.runConfig.REVIVE_FALL_RESTORE_HP)
        : 1;
      if (!isFinite(restoreHp) || restoreHp < 1) restoreHp = 1;
      this.player.hp = Math.min(this.player.maxHp, Math.max(1, Math.floor(restoreHp)));
      if (typeof this.player.activateShield === "function") {
        this.player.activateShield(this._getAnimTime(now));
      }
      this._clearFallingShieldPickups();
      this._flaskShardBursts = [];
      this.isFlaskShattered = false;
      this.shatterEndsAt = 0;
      this.reviveOfferActive = false;
      this.reviveGraceEndsAt = 0;
      this._shatterGraceOutcome = null;
      if (this.eventBus) this.eventBus.emit("fall:revive", {});
      if (typeof this.options.onReviveCompleted === "function") {
        this.options.onReviveCompleted({ stage: "fall", context: "live" });
      }
      this.resume(now);
      return true;
    }

    _onBoulderHit(obj) {
      var hadShield = this.player.hasShield;
      if (hadShield) {
        if (typeof this.options.onBoulderBlocked === "function") {
          this.options.onBoulderBlocked();
        }
      } else if (typeof this.options.onBoulderHit === "function") {
        this.options.onBoulderHit();
      }
      var hitTime = this.fallPhysics && this.fallPhysics._frameTime != null
        ? this.fallPhysics._frameTime
        : (typeof performance !== "undefined" ? performance.now() : Date.now());
      this.player.applyHazardDamage(this._getAnimTime(hitTime));
      if (typeof this.options.onBoulderImpact === "function") {
        this.options.onBoulderImpact();
      }
      if (hadShield) {
        obj.shieldBlockedBoulderHit = true;
        if (this.eventBus) this.eventBus.emit("fall:shield_block", { obj: obj });
        return;
      }
      var isHpDepleted = this.player.hp <= this.player.minHp;
      // Shard burst: small spray on HP damage, full burst on flask break (never on shield).
      // Use anim-clock time (pause-adjusted) so bursts still play after revive pause.
      if (
        this.player
        && this.fallRenderer
        && typeof this.fallRenderer._getFlaskDims === "function"
      ) {
        var severity = getFlaskDamageSeverity(this.player.hp);
        var dims = this.fallRenderer._getFlaskDims(this.runHeight);
        var burstAt = this._getAnimTime(hitTime);
        var burst = createFlaskShardBurst({
          drawX: this.player.x - dims.flaskW / 2,
          drawY: dims.flaskTopY,
          drawW: dims.flaskW,
          drawH: dims.flaskH,
        }, severity > 0 ? severity - 1 : 1, burstAt, {
          countMin: isHpDepleted
            ? FLASK_SHARD_BURST_COUNT_MIN
            : FLASK_SHARD_BURST_DAMAGE_COUNT_MIN,
          countMax: isHpDepleted
            ? FLASK_SHARD_BURST_COUNT_MAX
            : FLASK_SHARD_BURST_DAMAGE_COUNT_MAX,
        });
        if (burst) this._flaskShardBursts.push(burst);
      }
      if (this.eventBus) this.eventBus.emit("fall:boulder", { obj: obj, isHpDepleted: isHpDepleted });
      if (isHpDepleted && !this.isFlaskShattered) {
        var now = hitTime;
        this._beginReviveGrace(now, this._canOfferReviveNow() ? "revive" : "fail");
      }
    }

    _resetFallCallouts() {
      this._playElapsedMs = 0;
      this._fallCallout = null;
      this._dayCalloutShown = false;
      this._catchCalloutCount = 0;
      this._pendingCreatureCatches = 0;
    }

    _tickFallCallout() {
      var stepped = stepFallCallout(this._fallCallout, {
        playing: this.state === "playing",
        blocked: !!(this.isFlaskShattered || this.isRunEndingAftermath),
        playElapsedMs: this._playElapsedMs || 0,
        cycleIndex: this.runConfig ? this.runConfig.cycleIndex : null,
        movementHintOffered: this._shouldOfferMovementHint(),
        movementHintVisible: this._isMovementHintVisible(),
        dayCalloutShown: !!this._dayCalloutShown,
        catchCount: this._catchCalloutCount || 0,
        creatureCatches: this._pendingCreatureCatches || 0,
      });
      this._pendingCreatureCatches = 0;
      this._fallCallout = stepped.active;
      this._dayCalloutShown = stepped.dayCalloutShown;
      this._catchCalloutCount = stepped.catchCount;
    }

    _getFallCalloutDraw() {
      var callout = this._fallCallout;
      if (!callout || !callout.text) return null;
      var elapsed = this._playElapsedMs || 0;
      return {
        text: callout.text,
        ageMs: Math.max(0, elapsed - callout.startedAtMs),
        durationMs: Math.max(1, callout.endsAtMs - callout.startedAtMs),
      };
    }

    _render(currentTime) {
      var ctx = this.ctx;
      if (!ctx) return;
      var playfield = this.options.playfield;
      if (playfield && typeof playfield.beginFrame === "function") {
        playfield.beginFrame();
      }
      if (typeof this.options.onDrawSky === "function") {
        var skyStart = typeof performance !== "undefined" ? performance.now() : currentTime;
        this.options.onDrawSky(ctx, currentTime);
        this._devFrameSkyMs = (typeof performance !== "undefined" ? performance.now() : currentTime) - skyStart;
      }
      var drawStart = typeof performance !== "undefined" ? performance.now() : currentTime;
      var runWidth = this.runWidth;
      var runHeight = this.runHeight;

      var ox = this.runOriginX || 0;
      var oy = this.runOriginY || 0;
      var canvasW = this.options.canvasWidth;
      var canvasH = this.options.canvasHeight;
      if (canvasH == null && ctx.canvas) {
        var dpr = (typeof window !== "undefined" && ctx.canvas.width && ctx.canvas.clientWidth)
          ? ctx.canvas.width / ctx.canvas.clientWidth
          : 1;
        canvasH = ctx.canvas.height / (dpr > 0 ? dpr : 1);
      }
      var cfg = this.runConfig || {};
      var topOverflow = computeFallDrawTopOverflowPx(runWidth, cfg, runHeight);
      var clipBottomLocal = canvasH != null && isFinite(canvasH)
        ? Math.max(runHeight, canvasH - oy)
        : runHeight;
      this._syncMovementHintDom();
      var animTime = this._getAnimTime(currentTime);
      var viewRight = canvasW != null && isFinite(canvasW) ? canvasW - ox : runWidth;
      var viewBottom = canvasH != null && isFinite(canvasH) ? canvasH - oy : runHeight;
      var shardViewport = {
        left: -ox,
        top: -oy,
        right: viewRight,
        bottom: viewBottom,
      };
      var shardGravityPx = getFlaskShardGravityPx(runHeight, cfg);
      this._flaskShardBursts = pruneFlaskShardBursts(
        this._flaskShardBursts,
        animTime,
        shardViewport,
        shardGravityPx
      );
      var drawArgs = {
        scale: this.scale,
        runWidth: runWidth,
        runHeight: runHeight,
        clipOriginX: ox,
        clipCanvasWidth: canvasW,
        clipTopOverflow: topOverflow,
        clipBottomLocal: clipBottomLocal,
        bodyEntries: this.fallPhysics ? this.fallPhysics.bodies : [],
        player: this.player,
        currentTime: this._getSimTime(currentTime),
        animTime: animTime,
        flaskShardBursts: this._flaskShardBursts,
        flaskShardGravityPx: shardGravityPx,
        flaskShardViewport: shardViewport,
        state: this.state,
        countdownRemaining: this.countdownRemaining,
        showMovementRange: this._shouldShowMovementRange(),

        countdownStartedAt: this.countdownStartedAt,
        playingStartedAt: this.playingStartedAt,
        catcherFlaskIntroAlpha: typeof this.options.getCatcherFlaskIntroAlpha === "function"
          ? this.options.getCatcherFlaskIntroAlpha()
          : 1,
        playfield: playfield || null,
        worldOrigin: { x: ox, y: oy },
        fallPhysics: this.fallPhysics,
        fallCallout: this._getFallCalloutDraw(),
      };
      // Offset the run content into the centered play box (X = column, Y = box top)
      // so falling items + the catch flask sit in the layout box, not the screen top.
      if ((ox || oy) && typeof ctx.save === "function" && typeof ctx.translate === "function") {
        ctx.save();
        ctx.translate(ox, oy);
        this.fallRenderer.draw(ctx, drawArgs);
        if (typeof this.options.onDrawFlaskPhysicsDebug === "function") {
          this.options.onDrawFlaskPhysicsDebug(ctx);
        }
        if (typeof ctx.restore === "function") ctx.restore();
      } else {
        this.fallRenderer.draw(ctx, drawArgs);
        if (typeof this.options.onDrawFlaskPhysicsDebug === "function") {
          this.options.onDrawFlaskPhysicsDebug(ctx);
        }
      }

      // Flask panel (Stage II side)
      if (typeof this.options.onDrawFlask === "function") {
        this.options.onDrawFlask(ctx, this.player ? this.player.hp : 3);
      }
      // Full-board glue: dock strip (Fall), screen wash, column seams, dock riser (Game-owned).
      if (typeof this.options.onDrawStructuralAmbient === "function") {
        this.options.onDrawStructuralAmbient(ctx);
      }
      this._devFrameDrawMs = (typeof performance !== "undefined" ? performance.now() : currentTime) - drawStart;
      if (typeof this.options.onPresent === "function") {
        this.options.onPresent();
      }
    }

    _isSettleVisuallyIdle(currentTime) {
      if (currentTime < this.catchFlashUntil) return false;
      if (!this.fallPhysics) return true;
      if (typeof this.fallPhysics.isRunwayVisuallyIdle === "function") {
        return this.fallPhysics.isRunwayVisuallyIdle(currentTime);
      }
      return !this.fallPhysics.bodies || this.fallPhysics.bodies.length === 0;
    }

    _startRunEndingAftermath(currentTime) {
      this.isRunEndingAftermath = true;
      var aftermathMs = Math.max(0, getRunEndAftermathMs(this.runConfig));
      this.runEndAftermathEndsAt = currentTime + aftermathMs;
      // Keep flask suction active so in-flight pickups above the mouth can still be caught.
      if (aftermathMs === 0) {
        this.finish();
      }
    }

    finish() {
      this.state = "finished";
      if (this.inputManager) {
        this.inputManager.detach();
        this.inputManager = null;
      }
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      if (this.fallPhysics) {
        this.fallPhysics.destroy();
        this.fallPhysics = null;
      }
      if (this.eventBus) this.eventBus.emit("fall:complete", this.getResults());
      if (this.onComplete) this.onComplete(this.getResults());
    }

    fail(reason) {
      if (this.state === "finished" || this.state === "failed" || this.state === "idle") return;
      this.state = "failed";
      if (this.inputManager) {
        this.inputManager.detach();
        this.inputManager = null;
      }
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      if (this.fallPhysics) {
        this.fallPhysics.destroy();
        this.fallPhysics = null;
      }
      if (this.eventBus) this.eventBus.emit("fall:failed", { reason: reason || "unknown" });
    }
  }